import type { TNavigationLocales } from 'src/types/locales'
import { ESmsLogSource, type TSmsLog } from 'src/types/sms-log'
import { getTranslatedLabel } from 'src/utils/locale.util'
import {
  EHumanReviewRequestProblem,
  type THumanReviewDetail,
  type THumanReviewRequest,
} from '../human-review-requests.type'

const AGGREGATED_PROBLEMS = new Set([
  EHumanReviewRequestProblem.DECLARED_COMPLICATION,
  EHumanReviewRequestProblem.MISSING_DOCUMENT,
])

const getCreatedAt = (request: THumanReviewRequest): number => Date.parse(request.createdAt)

const sortByMostRecent = <T extends THumanReviewRequest>(requests: T[]): T[] =>
  [...requests].sort((requestA, requestB) => getCreatedAt(requestB) - getCreatedAt(requestA))

// An unknown problem always comes with a conversation, which is often empty (no form, or no answer yet)
const hasConversationAnswers = (request: THumanReviewRequest): boolean =>
  !!request.PatientConversation?.questions.some((question) => question.patientAnswer !== null)

// Whether the problem displays the conversation questions
export const hasConversationDetail = (request: THumanReviewRequest): boolean => {
  switch (request.problem) {
    case EHumanReviewRequestProblem.UNKNOWN:
      return hasConversationAnswers(request)
    case EHumanReviewRequestProblem.DECLARED_COMPLICATION:
    case EHumanReviewRequestProblem.MISSING_DOCUMENT:
    case EHumanReviewRequestProblem.APPOINTMENT_CANCELLED:
    case EHumanReviewRequestProblem.APPOINTMENT_RESCHEDULE_REQUESTED:
    case EHumanReviewRequestProblem.MISSING_DISCHARGE_COMPANION:
    case EHumanReviewRequestProblem.NO_RESPONSE:
    case EHumanReviewRequestProblem.INCOMPLETE_QUESTIONNAIRE:
      return !!request.PatientConversation && request.PatientConversation.questions.length > 0
    default:
      return false
  }
}

const isSentByPatient = (smsLog: TSmsLog): boolean =>
  smsLog.inbound || smsLog.source === ESmsLogSource.PATIENT_APP

// SMS are only displayed when the conversation has no answer to show
const getUnknownProblemSmsLog = (request: THumanReviewRequest): TSmsLog | null =>
  hasConversationAnswers(request) ? null : request.SmsLog

const getUnknownProblemPreviousSmsLog = (request: THumanReviewRequest): TSmsLog | null =>
  !hasConversationAnswers(request) &&
  request.PreviousOutboundSmsLog &&
  !isSentByPatient(request.PreviousOutboundSmsLog)
    ? request.PreviousOutboundSmsLog
    : null

const hasUnknownProblemContext = (request: THumanReviewRequest): boolean =>
  !!(
    request.PatientMedAlert ||
    request.Variable ||
    hasConversationAnswers(request) ||
    getUnknownProblemSmsLog(request) ||
    getUnknownProblemPreviousSmsLog(request)
  )

const PROBLEMS_WITH_MED_ALERT = new Set([
  EHumanReviewRequestProblem.DECLARED_COMPLICATION,
  EHumanReviewRequestProblem.UNKNOWN,
])

const PROBLEMS_WITH_VARIABLE = new Set([
  EHumanReviewRequestProblem.MISSING_DOCUMENT,
  EHumanReviewRequestProblem.UNKNOWN,
])

// What a request displays in the card, besides the conversation questions
export type TRequestDetails = {
  // The patient feedback replaces the problem label when the request comes with some context
  isPatientFeedback: boolean
  medAlert: THumanReviewRequest['PatientMedAlert']
  variable: THumanReviewRequest['Variable']
  // In display order
  smsLogs: TSmsLog[]
  hasNoContext: boolean
}

export const getRequestDetails = (request: THumanReviewRequest): TRequestDetails => {
  const isUnknown = request.problem === EHumanReviewRequestProblem.UNKNOWN
  const hasContext = isUnknown && hasUnknownProblemContext(request)

  const smsLogs = (() => {
    if (isUnknown) {
      return [getUnknownProblemPreviousSmsLog(request), getUnknownProblemSmsLog(request)].filter(
        (smsLog) => smsLog !== null,
      )
    }
    return request.problem === EHumanReviewRequestProblem.SMS_NOT_RECEIVED && request.SmsLog
      ? [request.SmsLog]
      : []
  })()

  return {
    isPatientFeedback: hasContext,
    medAlert: PROBLEMS_WITH_MED_ALERT.has(request.problem) ? request.PatientMedAlert : null,
    variable: PROBLEMS_WITH_VARIABLE.has(request.problem) ? request.Variable : null,
    smsLogs,
    hasNoContext: isUnknown && !hasContext,
  }
}

// Current date of the appointment: its convocation date, else the appointment date itself.
// The appointment date is not the convocation time (e.g. SMS sent with an offset), hence the distinct kind.
export type TNewConvocationDate = {
  date: string
  kind: 'convocation' | 'appointment'
}

export const getNewConvocationDate = (
  patientMedEvent: THumanReviewDetail['patientMedEvent'],
): TNewConvocationDate | null => {
  if (patientMedEvent.convocationDate) return { date: patientMedEvent.convocationDate, kind: 'convocation' }
  return patientMedEvent.date ? { date: patientMedEvent.date, kind: 'appointment' } : null
}

export type TConvocationDateChange = {
  // Last convocation SMS sent before the change, received by the patient with the old date. Null for alerts created before it was provided.
  smsLog: TSmsLog | null
  newDate: TNewConvocationDate | null
}

// Null when there is nothing to display, so that the card does not show an empty block
export const getConvocationDateChange = (
  request: THumanReviewRequest,
  newDate: TNewConvocationDate | null,
): TConvocationDateChange | null =>
  request.problem === EHumanReviewRequestProblem.CONVOCATION_DATE_CHANGED && (request.SmsLog || newDate)
    ? { smsLog: request.SmsLog, newDate }
    : null

// What the card is about: a conversation with the patient, or a message sent by a workflow.
// They never coexist, and a card can have neither (e.g. a patient message without workflow).
export type TCardSource = {
  kind: 'conversation' | 'workflow'
  label: string
  sentAt: string | null
}

export const getCardSource = (
  requests: THumanReviewRequest[],
  locale: TNavigationLocales,
): TCardSource | null => {
  const conversation = requests.find((request) => request.PatientConversation)?.PatientConversation
  if (conversation) {
    return {
      kind: 'conversation',
      label: conversation.formLabel ?? '',
      sentAt: conversation.firstSmsLogSentAt,
    }
  }

  const workflowSmsLog = requests.find((request) => request.SmsLog?.workflowIntlLabel)?.SmsLog
  if (workflowSmsLog?.workflowIntlLabel) {
    return {
      kind: 'workflow',
      label: getTranslatedLabel(locale, workflowSmsLog.workflowIntlLabel) ?? '',
      sentAt: workflowSmsLog.sentAt,
    }
  }
  return null
}

// From this number of conversations / workflow messages, the cards are collapsed by default
const COLLAPSED_BY_DEFAULT_CARDS_COUNT = 3

export const shouldCollapseCardsByDefault = (
  cards: TProblemCardGroup<THumanReviewRequest>[],
  locale: TNavigationLocales,
): boolean =>
  cards.filter(({ requests }) => getCardSource(requests, locale)).length >= COLLAPSED_BY_DEFAULT_CARDS_COUNT

export type TProblemCardGroup<T extends THumanReviewRequest> = {
  // Stable while requests are added to the group, so that the card keeps its state
  key: string
  requests: T[]
}

// One card per conversation. Requests without conversation get their own card,
// except for the aggregated problems which are displayed together.
export const groupIntoProblemCards = <T extends THumanReviewRequest>(
  requests: T[],
): TProblemCardGroup<T>[] => {
  const requestsByKey = new Map<string, T[]>()
  requests.forEach((request) => {
    const key =
      request.PatientConversationId !== null
        ? `conversation-${request.PatientConversationId}`
        : AGGREGATED_PROBLEMS.has(request.problem)
          ? `problem-${request.problem}`
          : `request-${request.id}`
    requestsByKey.set(key, [...(requestsByKey.get(key) ?? []), request])
  })

  return [...requestsByKey.entries()]
    .map(([key, groupRequests]) => ({ key, requests: sortByMostRecent(groupRequests) }))
    .sort((groupA, groupB) => getCreatedAt(groupB.requests[0]) - getCreatedAt(groupA.requests[0]))
}

export type TProblemRow<T extends THumanReviewRequest> = {
  request: T
  // Requests displayed by the row: all the problems of the same type for aggregated problems, else the request only
  requests: T[]
}

// The requests are all from the same group
export const buildProblemRows = <T extends THumanReviewRequest>(requests: T[]): TProblemRow<T>[] => {
  const aggregatedProblems = new Set<EHumanReviewRequestProblem>()

  return requests.flatMap((request) => {
    if (!AGGREGATED_PROBLEMS.has(request.problem)) return [{ request, requests: [request] }]
    if (aggregatedProblems.has(request.problem)) return []
    aggregatedProblems.add(request.problem)
    return [{ request, requests: requests.filter(({ problem }) => problem === request.problem) }]
  })
}
